import { useEffect, useState } from 'react';
import { T } from '../../components/text/TextEdit';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { api } from '../../lib/api';
import { SectionCard, Skeleton } from '../pipeline/ui';
import { formatCredits } from '../../lib/credits';

function Stat({ label, sub, value, testId }) {
  return (
    <div className="rounded-md border border-border bg-card p-4" data-testid={`${testId}-card`}>
      <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="mt-1 text-lg font-data text-foreground" data-testid={testId}>{value}</div>
      {sub && <div className="mt-1 text-xs text-muted-foreground">{sub}</div>}
    </div>
  );
}

// READ-ONLY god money view: revenue / liability / promo split, tenants × SKU rollup, and the
// 30-day renewals lever. Accepts data props so it can be unit-tested without the network.
// Three sections, each with a home (Ali, 2026-08-05): show='main' = the revenue/liability
// overview only (credit dashboard); show='tenants' = the Tenants x SKU table only (its own
// tab); show='renewals' = the 30-day renewals card only (Plans tab). Unset renders all (tests).
export function MoneyDashboardView({ overview, tenants = [], renewals = [], peg = 0.01, show }) {
  const { t } = useTranslation();
  const p = overview?.credit_usd_peg ?? peg;
  const rev = overview?.revenue?.purchased_credits ?? 0;
  const promo = overview?.promo_cost || { total: 0, byOrigin: {} };
  const liab = overview?.liability || { total: 0, purchased: 0, promo: 0 };
  const originLabel = (o) => ({
    granted: t('moneyDash.originGranted'),
    referral: t('moneyDash.originReferral'),
    gift: t('moneyDash.originGift'),
  }[o] || o);
  const promoOrigins = Object.entries(promo.byOrigin || {}).filter(([o]) => o !== 'purchased');

  return (
    <div className="flex flex-col gap-6" data-testid="money-dashboard">
      {(!show || show === 'main') && (
      <SectionCard title={t('moneyDash.title')} testId="money-overview-card">
        <p className="text-sm text-muted-foreground -mt-2"><T k="moneyDash.hint" /></p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-2">
          <Stat label={<T k="moneyDash.revenueTitle" />} sub={t('moneyDash.revenueSub')} value={formatCredits(rev, p)} testId="money-revenue" />
          <Stat label={<T k="moneyDash.promoTitle" />} sub={t('moneyDash.promoSub')} value={formatCredits(promo.total, p)} testId="money-promo" />
          <Stat label={<T k="moneyDash.liabilityTitle" />} sub={t('moneyDash.liabilitySub')} value={formatCredits(liab.total, p)} testId="money-liability" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
          <Stat label={<T k="moneyDash.liabilityPurchased" />} value={formatCredits(liab.purchased, p)} testId="money-liability-purchased" />
          <Stat label={<T k="moneyDash.liabilityPromo" />} value={formatCredits(liab.promo, p)} testId="money-liability-promo" />
        </div>
        {promoOrigins.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2" data-testid="money-promo-breakdown">
            {promoOrigins.map(([o, c]) => (
              <span key={o} className="inline-flex px-2.5 py-0.5 rounded-full text-xs border bg-muted text-muted-foreground border-border" data-testid={`money-origin-${o}`}>
                {originLabel(o)}: {formatCredits(c, p)}
              </span>
            ))}
          </div>
        )}
      </SectionCard>

      )}
      {(!show || show === 'tenants') && (
      <SectionCard title={t('moneyDash.tenantsTitle')} testId="money-tenants-card">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="money-tenants-table">
            <thead>
              <tr className="text-start text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                <th className="text-start py-2 pe-4"><T k="moneyDash.colTenant" /></th>
                <th className="text-start py-2 pe-4"><T k="moneyDash.colSku" /></th>
                <th className="text-start py-2 pe-4"><T k="moneyDash.colBalance" /></th>
                <th className="text-start py-2 pe-4"><T k="moneyDash.colPurchased" /></th>
                <th className="text-start py-2 pe-4"><T k="moneyDash.colPromo" /></th>
                <th className="text-start py-2"><T k="moneyDash.colSpend" /></th>
              </tr>
            </thead>
            <tbody className="font-data">
              {tenants.length === 0 ? (
                <tr><td colSpan="6" className="py-3 text-muted-foreground" data-testid="money-tenants-empty"><T k="moneyDash.empty" /></td></tr>
              ) : tenants.map((tn) => (
                <tr key={tn.user_id} className="border-b border-border" data-testid={`money-tenant-${tn.user_id}`}>
                  <td className="py-2 pe-4 text-foreground">{tn.email}</td>
                  <td className="py-2 pe-4 text-muted-foreground">{tn.sku || t('moneyDash.empty')}</td>
                  <td className="py-2 pe-4">{formatCredits(tn.balance, p)}</td>
                  <td className="py-2 pe-4">{formatCredits(tn.purchased, p)}</td>
                  <td className="py-2 pe-4">{formatCredits(tn.promo, p)}</td>
                  <td className="py-2">{formatCredits(tn.spend_period, p)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </SectionCard>

      )}
      {(!show || show === 'renewals') && (
      <SectionCard title={t('moneyDash.renewalsTitle')} testId="money-renewals-card">
        <p className="text-sm text-muted-foreground -mt-2"><T k="moneyDash.renewalsSub" /></p>
        {renewals.length === 0 ? (
          <p className="text-sm text-muted-foreground mt-2" data-testid="money-renewals-empty"><T k="moneyDash.noRenewals" /></p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="money-renewals-table">
              <thead>
                <tr className="text-start text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                  <th className="text-start py-2 pe-4"><T k="moneyDash.colUser" /></th>
                  <th className="text-start py-2 pe-4"><T k="moneyDash.colPlan" /></th>
                  <th className="text-start py-2 pe-4"><T k="moneyDash.colExpiry" /></th>
                  <th className="text-start py-2"><T k="moneyDash.colCredits" /></th>
                </tr>
              </thead>
              <tbody className="font-data">
                {renewals.map((r, i) => (
                  <tr key={`${r.user_id}-${i}`} className="border-b border-border" data-testid={`money-renewal-${i}`}>
                    <td className="py-2 pe-4 text-foreground">{r.email || r.user_id}</td>
                    <td className="py-2 pe-4">{r.plan_label || t('moneyDash.empty')}</td>
                    <td className="py-2 pe-4 text-muted-foreground">{r.cycle_end ? new Date(r.cycle_end).toLocaleDateString() : ''}</td>
                    <td className="py-2">{formatCredits(r.credits, p)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>
      )}
    </div>
  );
}

// Container: fetches the three read-only report actions on mount.
export default function MoneyDashboard({ show }) {
  const [overview, setOverview] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [renewals, setRenewals] = useState([]);

  useEffect(() => {
    api.tenantAdmin('money_overview').then(setOverview).catch((e) => toast.error(e.message));
    api.tenantAdmin('tenants_sku_rollup').then((r) => setTenants(r.tenants || [])).catch(() => {});
    api.tenantAdmin('renewals_30d').then((r) => setRenewals(r.renewals || [])).catch(() => {});
  }, []);

  if (!overview) return <Skeleton className="h-40 w-full" />;
  return <MoneyDashboardView overview={overview} tenants={tenants} renewals={renewals} peg={overview?.credit_usd_peg} show={show} />;
}
